import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {USERS} from '../../Pages/Home';
import { buildApiUrl } from "../../config/api";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  width: 'calc(100% - 32px)',
  maxWidth: 500,
};

export default function ModalBooking({
  selectedDate,
  refreshTime,
  slots,
  trainer,
  onBooked,
}) {
  const trainerName = USERS.find((item) => item.id === trainer)?.label;
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const [activeTime, setActiveTime] = React.useState(null);
  const [form, setForm] = React.useState({
    name: "",
    surname: "",
    phone: "",
    comment: "",
  });
  const [success, setSuccess] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [cancelLink, setCancelLink] = React.useState("");
  const [copyStatus, setCopyStatus] = React.useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setStep(0);
    setActiveTime(null);
    setSuccess(false);
    setForm({ name: "", surname: "", phone: "", comment: "" });
    setErrors({});
    setCancelLink("");
    setCopyStatus("");
  };

  const copyCancelLink = async () => {
    if (!cancelLink) return;

    // Method 1: Clipboard API (works on HTTPS + modern browsers incl. iOS 13.4+)
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(cancelLink);
        setCopyStatus("✓ Скопійовано");
        return;
      } catch {}
    }

    // Method 2: execCommand з setSelectionRange (для старих Android/desktop)
    try {
      const el = document.createElement("input");
      el.value = cancelLink;
      el.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0;font-size:16px;";
      document.body.appendChild(el);
      el.focus();
      el.select();
      el.setSelectionRange(0, 99999);
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      if (ok) {
        setCopyStatus("✓ Скопійовано");
        return;
      }
    } catch {}

    // Fallback: підказка вручну
    setCopyStatus("Натисніть на лінк → виділіть → скопіюйте");
  };

  React.useEffect(() => {
    if (!slots.length) {
      handleClose();
    }
  }, [slots])

  React.useEffect(() => {
    if (!open) {
      refreshTime();
    }
  }, [open, refreshTime])

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Поле обов'язкове";
    if (!form.surname.trim()) newErrors.surname = "Поле обов'язкове";

    // Валидация телефона: игнорируем все кроме цифр
    const digits = form.phone.replace(/\D/g, ""); // удаляем всё кроме цифр

    if (!form.phone.trim()) {
      newErrors.phone = "Поле обов'язкове";
    } else if (
      !(
        digits.length === 10 ||
        (digits.length === 12 && digits.startsWith("380"))
      )
    ) {
      newErrors.phone = "Невірний формат номера (вказати український номер)";
    }

    if (form.comment.length > 200)
      newErrors.comment = "Коментар занадто довгий";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const res = await fetch(buildApiUrl("/api/requests"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trainer: trainer || "KOKORIN",
          date: selectedDate,
          time: activeTime,
          name: form.name,
          surname: form.surname,
          phone: form.phone,
          comment: form.comment,
        }),
      });

      if (res.status === 400) {
        refreshTime();
        setStep(0);
      }

      if (res.ok) {
        const result = await res.json();
        const link = `${window.location.origin}/cancel/${result.cancelToken}`;
        setCancelLink(link);

        const stored = JSON.parse(localStorage.getItem("kokorin_bookings") || "[]");
        stored.push({
          cancelToken: result.cancelToken,
          trainer: result.trainer,
          trainerLabel: trainerName,
          date: result.date,
          time: result.time,
          name: result.name,
          surname: result.surname,
        });
        localStorage.setItem("kokorin_bookings", JSON.stringify(stored));

        setSuccess(true);
        setStep(2);
        if (onBooked) onBooked();
      }
    } catch (err) {
      alert("Помилка при записі: " + err.message);
    }
  };

  return (
    <div>
      <button
        onClick={handleOpen}
        className="button reserve"
        disabled={!selectedDate}
      >
        {selectedDate ? `Бронювати на ${selectedDate}` : "Оберіть дату"}
      </button>

      <Modal open={open} onClose={handleClose}>
        <div className="modal_container">
          {/* Шаг 0 — выбор слота */}
          {step === 0 && (
            <Box sx={style}>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                Оберіть час для {selectedDate}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                Тренер: {trainerName}
              </Typography>
              <FormGroup className="checkbox-list">
                {slots.map((item) => (
                  <FormControlLabel
                    key={item}
                    control={
                      <Checkbox
                        checked={activeTime === item}
                        className="checkbox"
                        onChange={() => setActiveTime(item)}
                      />
                    }
                    label={item}
                  />
                ))}
              </FormGroup>
              <button
                disabled={!activeTime}
                onClick={() => setStep(1)}
                className="button reserve"
              >
                Продовжити
              </button>
            </Box>
          )}

          {/* Шаг 1 — форма клиента */}
          {step === 1 && (
            <Box sx={style}>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                Введіть ваші дані
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                {trainerName} • {selectedDate} • {activeTime}
              </Typography>

              <TextField
                label="Ім'я"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                error={!!errors.name}
                helperText={errors.name}
              />

              <TextField
                label="Прізвище"
                value={form.surname}
                onChange={(e) => setForm({ ...form, surname: e.target.value })}
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                error={!!errors.surname}
                helperText={errors.surname}
              />

              <TextField
                label="Телефон"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                error={!!errors.phone}
                helperText={errors.phone}
              />

              <TextField
                label="Коментар"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 3 }}
                error={!!errors.comment}
                helperText={errors.comment}
              />

              <button onClick={handleSubmit} className="button reserve">
                Підтвердити запис
              </button>
            </Box>
          )}

          {/* Шаг 2 — успех */}
          {step === 2 && (
            <Box sx={style}>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                ✅ Ви успішно записались!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {trainerName} — {selectedDate}, {activeTime}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.7, fontSize: "12px" }}>
                Збережіть посилання для відміни запису:
              </Typography>
              <div className="cancel-link-box">
                <input
                  value={cancelLink}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
                <button onClick={copyCancelLink}>
                  {copyStatus || "Копіювати"}
                </button>
              </div>
              <button
                onClick={handleClose}
                className="button reserve"
                style={{ marginTop: 16 }}
              >
                Закрити
              </button>
            </Box>
          )}
        </div>
      </Modal>

      {success && (
        <div className="alert-absolute">
          <Alert severity="success">Ви успішно записались.</Alert>
        </div>
      )}
    </div>
  );
}
